/**
 * ServiceGroupCard — expandable card showing a group and its member services.
 * Used in ServicesList. Each member row shows status, access icons, quick actions.
 */
import React, { useState } from 'react';
import { TrashIcon, PencilSquareIcon, PlusIcon, ArrowTopRightOnSquareIcon, MapIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import ServicePicker from './ServicePicker';
import ServiceGroupEditModal from './ServiceGroupEditModal';
import ServiceGroupMap from './ServiceGroupMap';

const STATUS_DOT = {
  running:     { color: '#10b981', label: 'Running' },
  stopped:     { color: '#ef4444', label: 'Parado' },
  maintenance: { color: '#f59e0b', label: 'Mantenimiento' },
};
const CRIT_COLORS = {
  critical: { color: '#ef4444' },
  high:     { color: '#f97316' },
  medium:   { color: '#f59e0b' },
  low:      { color: '#6b7280' },
};
const ACCESS_ICONS = { ssh: '🔒', rdp: '🖥️', https: '🌐', http: '🌐', api: '🔌', database: '🗄️', vpn: '🔐', terminal_server: '🖥️', console: '🖥️' };

export default function ServiceGroupCard({ group, companyId, onEditService, onGroupChange }) {
  const [expanded, setExpanded] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [showMap, setShowMap] = useState(false);

  const handleDeleteGroup = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`¿Eliminar el grupo "${group.name}"? Los servicios no se eliminarán.`)) return;
    setDeleting(true);
    try {
      await api.deleteServiceGroup(group.id, companyId);
      onGroupChange?.();
    } catch (err) {
      alert(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleAddMember = async (svc) => {
    setAddError(null);
    try {
      await api.addServiceGroupMember(group.id, { service_id: svc.id }, companyId);
      onGroupChange?.();
    } catch (err) {
      setAddError(err.message);
    }
  };

  const handleRemoveMember = async (e, member) => {
    e.stopPropagation();
    if (!window.confirm(`¿Quitar "${member.service_name}" del grupo?`)) return;
    try {
      await api.removeServiceGroupMember(group.id, member.id, companyId);
      onGroupChange?.();
    } catch (err) {
      alert(err.message);
    }
  };

  const members = group.members || [];
  const allRunning = members.length > 0 && members.every(m => m.service_status === 'running');
  const anyDown = members.some(m => m.service_status === 'stopped');

  const groupStatusColor = anyDown ? '#ef4444' : allRunning ? '#10b981' : '#f59e0b';

  return (
    <div style={{
      border: `1px solid var(--border)`,
      borderLeft: `3px solid ${groupStatusColor}`,
      borderRadius: 8,
      background: 'var(--bg-card)',
      overflow: 'hidden',
      marginBottom: 8,
    }}>
      {/* Group header */}
      <div
        onClick={() => setExpanded(e => !e)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 14px', cursor: 'pointer',
          background: 'var(--bg-toolbar)',
          borderBottom: expanded ? '1px solid var(--border)' : 'none',
        }}
      >
        <span style={{ fontSize: '1.2rem' }}>{group.icon}</span>
        <div style={{ flex: 1 }}>
          <span style={{ fontWeight: 700, fontSize: '0.92rem', color: 'var(--text-primary)' }}>
            {group.name}
          </span>
          {group.description && (
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginLeft: 8 }}>
              {group.description}
            </span>
          )}
        </div>
        <span style={{
          fontSize: '0.72rem', fontWeight: 600,
          background: 'var(--bg-card)', color: 'var(--text-secondary)',
          padding: '1px 7px', borderRadius: 10, border: '1px solid var(--border)',
        }}>
          {members.length} servicio{members.length !== 1 ? 's' : ''}
        </span>
        <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
          <button type="button" className="wol-action-btn"
            onClick={() => setShowMap(true)} title="Ver mapa completo">
            <MapIcon style={{ width: 14, height: 14 }} />
          </button>
          <button type="button" className="wol-action-btn"
            onClick={() => setEditing(true)} title="Editar grupo">
            <PencilSquareIcon style={{ width: 13, height: 13 }} />
          </button>
          <button type="button" className="wol-action-btn wol-action-btn--delete"
            onClick={handleDeleteGroup} disabled={deleting} title="Eliminar grupo">
            <TrashIcon style={{ width: 13, height: 13 }} />
          </button>
        </div>
        <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginLeft: 4 }}>
          {expanded ? '▲' : '▼'}
        </span>
      </div>

      {/* Member rows */}
      {expanded && (
        <div>
          {members.length === 0 && (
            <div style={{ padding: '10px 16px', fontSize: '0.82rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
              Sin servicios. Usa «Añadir servicio» abajo.
            </div>
          )}
          {members.map((m, idx) => {
            const dot = STATUS_DOT[m.service_status] || { color: '#94a3b8', label: m.service_status };
            const crit = CRIT_COLORS[m.service_criticality] || {};
            return (
              <div key={m.id} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 14px',
                borderBottom: idx < members.length - 1 ? '1px solid var(--border)' : 'none',
                fontSize: '0.82rem',
              }}>
                {/* Status dot */}
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: dot.color, flexShrink: 0, display: 'inline-block' }} title={dot.label} />

                {/* Name + role */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <button type="button" onClick={() => onEditService?.(m.service_id)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left', fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.82rem' }}>
                    {m.service_name}
                  </button>
                  {m.role && (
                    <span style={{ marginLeft: 6, fontSize: '0.7rem', color: 'var(--text-muted)', background: 'var(--bg-toolbar)', padding: '0 5px', borderRadius: 3, border: '1px solid var(--border)' }}>
                      {m.role}
                    </span>
                  )}
                </div>

                {/* Type */}
                <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                  {m.service_type_label || m.service_type}
                </span>

                {/* Criticality */}
                {m.service_criticality && m.service_criticality !== 'low' && (
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, color: crit.color }}>
                    ●
                  </span>
                )}

                {/* Access icons */}
                {m.access_point_count > 0 && (
                  <span style={{ fontSize: '0.72rem', color: 'var(--accent)', fontWeight: 600 }}>
                    🔗 {m.access_point_count}
                  </span>
                )}

                {/* Quick URL */}
                {m.service_url && (
                  <a href={m.service_url} target="_blank" rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    style={{ color: 'var(--accent)', lineHeight: 1 }}
                    title={m.service_url}>
                    <ArrowTopRightOnSquareIcon style={{ width: 13, height: 13 }} />
                  </a>
                )}

                {/* Remove from group */}
                <button type="button" className="wol-action-btn wol-action-btn--delete"
                  onClick={(e) => handleRemoveMember(e, m)} title="Quitar del grupo">
                  <TrashIcon style={{ width: 12, height: 12 }} />
                </button>
              </div>
            );
          })}

          {/* Add service footer */}
          <div style={{ padding: '8px 14px', borderTop: members.length > 0 ? '1px solid var(--border)' : 'none' }}>
            {!adding ? (
              <button type="button"
                onClick={() => { setAdding(true); setAddError(null); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  background: 'none', border: '1px dashed var(--border)',
                  color: 'var(--accent)', borderRadius: 6, padding: '5px 10px',
                  fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', width: '100%',
                  justifyContent: 'center',
                }}>
                <PlusIcon style={{ width: 14, height: 14 }} />
                Añadir servicio
              </button>
            ) : (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
                    Añadir servicio al grupo
                  </span>
                  <button type="button" onClick={() => setAdding(false)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1 }}>
                    ✕
                  </button>
                </div>
                <ServicePicker
                  companyId={companyId}
                  excludeIds={members.map(m => m.service_id)}
                  onPick={handleAddMember}
                />
                {addError && <div style={{ fontSize: '0.75rem', color: '#ef4444', marginTop: 4 }}>⚠ {addError}</div>}
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 5 }}>
                  💡 Escribe un nombre nuevo para crear el servicio al instante.
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {editing && (
        <ServiceGroupEditModal
          group={group}
          companyId={companyId}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); onGroupChange?.(); }}
        />
      )}

      {showMap && (
        <ServiceGroupMap
          groupId={group.id}
          companyId={companyId}
          onClose={() => setShowMap(false)}
          onEditService={onEditService}
        />
      )}
    </div>
  );
}
